import type { IntakeData } from "./types";

export const FIELD = { sms: "smsConsent", voice: "voiceConsent" } as const;

/** Finalize payload: only ticked boxes; undefined when none. */
export const buildConsents = (data: IntakeData) => {
  const consents = (["sms", "voice"] as const).flatMap((channel) => {
    const id = data[FIELD[channel]]?.disclosureId;
    return id ? [{ channel, disclosureId: id }] : [];
  });
  return consents.length ? consents : undefined;
};

/** Split text into plain and http(s) URL parts (no HTML involved). Trailing punctuation stays plain. */
export const splitUrls = (text: string): { text: string; url?: string }[] =>
  text
    .split(/(https?:\/\/[^\s<>"]+)/g)
    .filter(Boolean)
    .flatMap((part) => {
      if (!/^https?:\/\//.test(part)) return [{ text: part }];
      const url = part.replace(/[.,;:!?)\]]+$/, "");
      const rest = part.slice(url.length);
      return rest ? [{ text: url, url }, { text: rest }] : [{ text: url, url }];
    });

/** Locale sent to the disclosure endpoint; the server falls back if it lacks one. */
export const disclosureLocale = (locale: string | undefined): "en" | "es" =>
  locale?.toLowerCase().startsWith("es") ? "es" : "en";

/** Box is ticked only if a disclosure loaded and it is the version that was ticked. */
export const isConsentChecked = (ticked: string | null | undefined, disclosureId: string | undefined) =>
  !!disclosureId && ticked === disclosureId;

/** Checked state to render: before the disclosure is ready (loading/error) show the stored tick; once ready require a version match. */
export const displayChecked = (ticked: string | null | undefined, disclosureId: string | undefined, ready: boolean) =>
  ready ? isConsentChecked(ticked, disclosureId) : !!ticked;

/**
 * Single decision for a settled disclosure load. `loadedId` is undefined when the load failed.
 * clear => emit `{[field]: null}`; notice => text shown next to the box. A fresh load always starts with notice "none".
 */
export const decideAfterLoad = ({
  ticked,
  loadedId,
}: {
  ticked: string | null | undefined;
  loadedId: string | undefined;
}): { clear: boolean; notice: "none" | "stale" | "error" } => {
  if (!shouldClearTick(ticked, loadedId)) return { clear: false, notice: "none" };
  return { clear: true, notice: loadedId ? "stale" : "error" };
};

/** After a fetch settles (disclosureId undefined = failure): clear a tick that no longer matches. */
export const shouldClearTick = (ticked: string | null | undefined, disclosureId: string | undefined) =>
  !!ticked && ticked !== disclosureId;

type Consents = NonNullable<ReturnType<typeof buildConsents>>;

/** Finalize request body; `consents` present only when at least one box was ticked. */
export const buildFinalizeBody = (consents?: Consents, submittedAt = new Date().toISOString()) => ({
  submittedAt,
  ...(consents?.length ? { consents } : {}),
});

/** Consent must be ticked in the current session: never persist it or restore it from a cache. */
export const stripConsents = <T extends Partial<IntakeData>>(data: T): T => {
  const { smsConsent, voiceConsent, ...rest } = data;
  void smsConsent;
  void voiceConsent;
  return rest as T;
};

export type CurrentDisclosure = {
  id: string;
  channel: string;
  locale: string;
  version: string;
  bodyText: string;
};

/** Accepts `{ data: {...} }` or a bare object; returns null for anything without id + bodyText. */
export const parseDisclosure = (parsed: unknown): CurrentDisclosure | null => {
  const root = parsed as { data?: unknown } | null | undefined;
  const d = (root?.data ?? root) as Partial<CurrentDisclosure> | null | undefined;
  return d && typeof d.id === "string" && d.id && typeof d.bodyText === "string" && d.bodyText
    ? (d as CurrentDisclosure)
    : null;
};

/** The one place the finalize call gets its consents, so both flows stay in sync. */
export const finalizeWithConsents = <R>(
  finalize: (formUid: string, consents?: ReturnType<typeof buildConsents>) => R,
  data: IntakeData & { formUid: string },
): R => finalize(data.formUid, buildConsents(data));

/** Focus the checkbox only right after a load that the Try again click started and that succeeded. */
export const shouldFocusAfterLoad = ({
  retryPending,
  status,
}: {
  retryPending: boolean;
  status: "loading" | "error" | "ready";
}) => retryPending && status === "ready";
